// =============================================================================
// rng.ts — seeded PRNG (mulberry32) + ActionLog (NEW — no Unity equivalent)
// All game randomness routes through createRng(seed) so replays are deterministic.
// NO console.debug in core (Q-B5 codex-locked).
// =============================================================================

import { deepFreeze } from "./_freeze.js";

// ---------------------------------------------------------------------------
// mulberry32 — fast 32-bit seeded PRNG
// Reference: https://github.com/bryc/code/blob/master/jshash/PRNGs.md
// ---------------------------------------------------------------------------

function mulberry32Step(state: number): { next: number; value: number } {
  // Canonical mulberry32 — state advances linearly by 0x6d2b79f5; mixing happens on output only.
  // Reference: https://github.com/bryc/code/blob/master/jshash/PRNGs.md#mulberry32
  const next = (state + 0x6d2b79f5) >>> 0;
  let t = next;
  t = Math.imul(t ^ (t >>> 15), t | 1) >>> 0;
  t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
  return { next, value };
}

// ---------------------------------------------------------------------------
// Rng interface + implementation
// ---------------------------------------------------------------------------

export interface Rng {
  /** Returns a float in [0, 1). */
  next(): number;
  /** Returns an integer in [min, max] (inclusive both ends). */
  nextInt(min: number, max: number): number;
  /** Picks a random element from a non-empty array. */
  pick<T>(arr: ReadonlyArray<T>): T;
  /** Returns a new shuffled copy of the array (Fisher-Yates). */
  shuffle<T>(arr: ReadonlyArray<T>): T[];
  /**
   * Creates a child Rng whose seed is derived from the parent's next random value
   * and the label (for namespacing). The parent advances one step.
   */
  fork(label: string): Rng;
}

/**
 * JSON-safe value for ActionLog payloads. Excludes functions, symbols, BigInt,
 * Map/Set, Date, undefined. Required for deterministic replay across runs +
 * WebSocket-safe (WEB.6) action-log transport.
 */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | { readonly [key: string]: JsonValue }
  | ReadonlyArray<JsonValue>;

export interface ActionLog {
  readonly entries: ReadonlyArray<{ kind: string; data: JsonValue; seq: number }>;
  /** Returns a new ActionLog with the entry appended. Immutable. */
  append(entry: { kind: string; data: JsonValue }): ActionLog;
}

// ---------------------------------------------------------------------------
// createRng
// ---------------------------------------------------------------------------

export function createRng(seed: number): Rng {
  if (!Number.isFinite(seed)) {
    throw new RangeError(`createRng: seed must be a finite number; got ${seed}`);
  }
  if (!Number.isInteger(seed)) {
    throw new RangeError(
      `createRng: seed must be an integer; got ${seed} (use Math.trunc(seed) if intentional)`,
    );
  }
  // Internal mutable state — scoped to this closure, NOT module-level.
  let state = seed >>> 0; // ensure unsigned 32-bit start

  function nextFloat(): number {
    const r = mulberry32Step(state);
    state = r.next;
    return r.value;
  }

  return {
    next(): number {
      return nextFloat();
    },

    nextInt(min: number, max: number): number {
      if (min > max) throw new RangeError(`nextInt: min (${min}) > max (${max})`);
      const range = max - min + 1;
      return Math.floor(nextFloat() * range) + min;
    },

    pick<T>(arr: ReadonlyArray<T>): T {
      if (arr.length === 0) throw new RangeError("pick: empty array");
      const idx = Math.floor(nextFloat() * arr.length);
      // Index is valid by construction (idx in [0, arr.length-1]); cast satisfies
      // noUncheckedIndexedAccess without rejecting legitimate `undefined` elements.
      return arr[idx] as T;
    },

    shuffle<T>(arr: ReadonlyArray<T>): T[] {
      const result: (T | undefined)[] = [...arr];
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(nextFloat() * (i + 1));
        const tmp = result[i];
        result[i] = result[j];
        result[j] = tmp;
      }
      // Cast is safe: we only swapped elements, none were removed or added.
      return result as T[];
    },

    fork(label: string): Rng {
      // Advance parent one step; XOR with label hash to differentiate forks.
      const base = nextFloat();
      // Simple djb2-style label hash mixed with base float bits.
      let labelHash = 5381;
      for (let k = 0; k < label.length; k++) {
        labelHash = ((labelHash * 33) ^ label.charCodeAt(k)) >>> 0;
      }
      const childSeed = (((base * 0x100000000) >>> 0) ^ labelHash) >>> 0;
      return createRng(childSeed);
    },
  };
}

// ---------------------------------------------------------------------------
// ActionLog — immutable append-only log
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// assertJsonSafe — runtime guard for ActionLog payloads
// ---------------------------------------------------------------------------

/**
 * Asserts at runtime that a value is JSON-safe (string/number/boolean/null/plain object/array).
 * Rejects functions, symbols, BigInt, Map, Set, Date, RegExp, and cyclic references.
 * Needed as defense-in-depth when callers bypass TS strict-mode with `as JsonValue`.
 */
function assertJsonSafe(
  v: unknown,
  path = "data",
  visited: WeakSet<object> = new WeakSet(),
): asserts v is JsonValue {
  if (v === null) return;
  const t = typeof v;
  if (t === "string" || t === "boolean") return;
  if (t === "number") {
    // NaN / Infinity / -Infinity are typeof "number" but lossy-serialize to
    // null via JSON.stringify — break replay determinism. Reject explicitly.
    if (!Number.isFinite(v)) {
      throw new TypeError(`ActionLog.${path}: ${String(v)} is not JSON-safe (non-finite number)`);
    }
    return;
  }
  if (t === "function" || t === "symbol" || t === "bigint" || t === "undefined") {
    throw new TypeError(`ActionLog.${path}: ${t} is not JSON-safe`);
  }
  if (t === "object") {
    if (v instanceof Map || v instanceof Set || v instanceof Date || v instanceof RegExp) {
      throw new TypeError(`ActionLog.${path}: ${(v as object).constructor.name} is not JSON-safe`);
    }
    if (visited.has(v as object)) {
      throw new TypeError(`ActionLog.${path}: cyclic reference detected`);
    }
    visited.add(v as object);
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) {
        assertJsonSafe(v[i], `${path}[${i}]`, visited);
      }
    } else {
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        assertJsonSafe(val, `${path}.${k}`, visited);
      }
    }
  }
}

export function createActionLog(): ActionLog {
  return makeLog([], 0);
}

function makeLog(
  entries: ReadonlyArray<{ kind: string; data: JsonValue; seq: number }>,
  nextSeq: number,
): ActionLog {
  const frozen = Object.freeze([...entries]);
  return Object.freeze({
    entries: frozen,
    append(entry: { kind: string; data: JsonValue }): ActionLog {
      assertJsonSafe(entry.data);
      const newEntry = Object.freeze({
        kind: entry.kind,
        data: deepFreeze(structuredClone(entry.data)),
        seq: nextSeq,
      });
      return makeLog([...entries, newEntry], nextSeq + 1);
    },
  });
}
